import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { RevxMeasure } from 'components/RevxMeasure';
import { MeasureType } from 'domains/measurementDomain';
import { useMount } from 'ahooks';
import { Form, Input, Select, TableColumnsType, Tooltip } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppSelector } from 'app/hooks';
import { OrgTypeEnum } from 'features/Platform/components/OrgAdmin/OrgTypeSelectModal';
import { useUser } from 'hooks';
import {
  JobconfigPaperNextIcon,
  JobconfigPaperPreIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxRadio, RevxRadioProps } from 'components/RevxRadio';
import { RevxTable } from 'components/RevxTable';
import {
  PlatformOptionPaper,
  PlatformOptionPaperScope,
  SearchScopeEnum,
} from 'domains/jobconfigDomain';
import { SKU } from 'domains/skuDomain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import './SearchTable.less';

const { Option } = Select;
const { Search } = Input;

export type SearchTableProps = {
  sku: Partial<SKU>;
  newOptionPapers: PlatformOptionPaperScope;
  onChange?: (sku: Partial<SKU>) => void;
};

type FormChangeProps = {
  searchScope: SearchScopeEnum;
  quantity: number[];
  search: string;
  category: string;
  finish: string;
  basisWeight: string;
  color: string;
  brand: string;
};

type SearchFormOption = {
  category: string[];
  finish: string[];
  basisWeight: string[];
  color: string[];
  brand: string[];
  subCategories?: string[];
};

type SearchFormProps = {
  searchScope: SearchScopeEnum;
  searchStr: string;
  category: string;
  finish: string;
  basisWeight: string;
  color: string;
  brand: string;
  subCategories?: string;
};

export const SearchTable = ({
  newOptionPapers,
  sku,
  onChange,
}: SearchTableProps) => {
  const { t } = useTranslation();
  const { columnTitle, basisWeight, basisWeightTitle } =
    useMeasurementUnit();
  const user = useUser();
  const orgList = useAppSelector((state) => state.org.list);
  const curOrg = useMemo(() => {
    const curOrgId = localStorage.getItem('org') || user?.orgId;
    return orgList.find((org) => org.id === Number(curOrgId));
  }, [orgList, user]);

  const [allTableData, setAllTableData] = useState<PlatformOptionPaper[]>();
  const [current, setCurrent] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [searchData, setSearchData] = useState<Partial<SearchFormProps>>({});
  const [form] = useForm();
  const pageSize = 5;

  const [searchForm, setSearchForm] = useState<SearchFormOption>();

  const paperRadioProps: RevxRadioProps = {
    unCheckIconSize: 'small',
    inSideRevxLayoutProps: {
      justify: 'left',
      align: 'center',
    },
    options: [
      {
        value: SearchScopeEnum.MY_PAPER,
        children: (
          <label className="search-scope">
            {curOrg?.orgType === OrgTypeEnum.CUSTOMER && curOrg?.parentName ? (
              `${curOrg.parentName} Papers`
            ) : (
              <Trans i18nKey="jobConfig.paperOption.myPapers"></Trans>
            )}
          </label>
        ),
      },
      {
        value: SearchScopeEnum.ALL_PAPER,
        children: (
          <label className="search-scope">
            <Trans i18nKey="jobConfig.paperOption.allPapers"></Trans>
          </label>
        ),
      },
    ],
  };

  const rounding = useCallback((num: number) => {
    if (num > (num | 0)) {
      return (num | 0) + 1;
    } else {
      return num;
    }
  }, []);

  useMount(async () => {
    setLoading(true);
    form.setFieldsValue({
      category: 'Category',
      finish: 'Finish',
      basisWeight: 'Basis Weight',
      color: 'Color',
      brand: 'Brand',
      searchScope: sku.paperSearchScope || SearchScopeEnum.ALL_PAPER,
    });
    // setNewOptionPapers([]);
  });

  const paperList = useMemo(() => {
    const paperSearchScope = searchData.searchScope || sku.paperSearchScope;
    if (paperSearchScope === SearchScopeEnum.MY_PAPER) {
      return newOptionPapers.myPapers;
    }
    return newOptionPapers.allPapers;
  }, [searchData, sku.paperSearchScope, newOptionPapers]);

  const defaultTableData = useCallback(() => {
    const categories = paperList
      ?.map((paper) => paper.category)
      .filter((item) => item)
      .sort((a, b) => (a || '').localeCompare(b || ''));
    const finishes = paperList
      ?.map((paper) => paper.finish)
      .filter((item) => item)
      .sort((a, b) => (a || '').localeCompare(b || ''));
    const basisWeights = paperList
      ?.map((paper) => paper.basisWeight)
      .filter((item) => item)
      .sort((a, b) => (a || '').localeCompare(b || ''));
    const colors = paperList
      ?.map((paper) => paper.color)
      .filter((item) => item)
      .sort((a, b) => (a || '').localeCompare(b || ''));
    const brands = paperList
      ?.map((paper) => paper.brand)
      .filter((item) => item)
      .sort((a, b) => (a || '').localeCompare(b || ''));
    const constraint1 =
      paperList
        ?.map((paper) => paper?.constraint1)
        .filter((item) => item)
        .sort((a, b) => (a || '').localeCompare(b || '')) || [];
    const constraint2 =
      paperList
        ?.map((paper) => paper?.constraint2)
        .filter((item) => item)
        .sort((a, b) => (a || '').localeCompare(b || '')) || [];
    const filterOption: SearchFormOption = {
      category: ['Category', ...Array.from(new Set(categories || []))],
      finish: ['Finish', ...Array.from(new Set(finishes || []))],
      basisWeight: ['Basis Weight', ...Array.from(new Set(basisWeights || []))],
      color: ['Color', ...Array.from(new Set(colors || []))],
      brand: ['Brand', ...Array.from(new Set(brands))],
      subCategories: [
        'Sub Categories',
        ...Array.from(new Set([...constraint1, ...constraint2])),
      ],
    };
    setSearchForm(filterOption);
    setAllTableData(paperList);
  }, [paperList]);

  useEffect(() => {
    if (newOptionPapers) {
      defaultTableData();
    }
  }, [newOptionPapers, defaultTableData]);

  const filterCallback = useCallback(
    (item: PlatformOptionPaper) => {
      const basisWeight =
        searchData.basisWeight === 'Basis Weight' ? '' : searchData.basisWeight;
      const brand = searchData.brand === 'Brand' ? '' : searchData.brand;
      const category =
        searchData.category === 'Category' ? '' : searchData.category;
      const subCategories =
        searchData.subCategories === 'Sub Categories' ||
        category !== 'wide_format'
          ? ''
          : searchData.subCategories;
      const color = searchData.color === 'Color' ? '' : searchData.color;
      const finish = searchData.finish === 'Finish' ? '' : searchData.finish;
      return (
        ((item.basisWeight || '')
          .toLowerCase()
          .replaceAll('_', ' ')
          ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.brand || '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.category || '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.color || '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.finish || '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.name || '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase()) ||
          (item.id + '')
            .toLowerCase()
            .replaceAll('_', ' ')
            ?.includes((searchData.searchStr || '').toLowerCase())) &&
        (item.basisWeight === basisWeight || !basisWeight) &&
        (item.brand === brand || !brand) &&
        (item.category === category || !category) &&
        (item.color === color || !color) &&
        (item.finish === finish || !finish) &&
        (item.constraint1 === subCategories || !subCategories)
      );
    },
    [searchData]
  );

  const allFilterTableData = useMemo(() => {
    setCurrent(0);
    return (allTableData || []).filter((item) => filterCallback(item));
  }, [allTableData, filterCallback]);

  const allPage = useMemo(() => {
    return rounding((allFilterTableData || []).length / pageSize);
  }, [allFilterTableData, rounding]);

  const tableSource = useMemo(() => {
    return (
      allFilterTableData.slice(current * pageSize, (current + 1) * pageSize) ||
      []
    );
  }, [allFilterTableData, current]);

  const renderColumns = useMemo(() => {
    const columns: TableColumnsType<PlatformOptionPaper> = [
      {
        title: <Trans i18nKey="jobConfig.paperOption.category" />,
        width: 160,
        dataIndex: 'category',
        render: (value) => {
          return (
            <span className="form-search-table-textCapitalize">
              {value?.replaceAll('_', ' ')}
            </span>
          );
        },
      },
      {
        title: <Trans i18nKey="jobConfig.paperOption.finish" />,
        width: 90,
        dataIndex: 'finish',
        render: (value) => {
          return (
            <span className="form-search-table-textCapitalize">
              {value?.replaceAll('_', ' ')}
            </span>
          );
        },
      },
      {
        title: basisWeightTitle(t('jobConfig.paperOption.basisWeight')),
        width: 100,
        dataIndex: 'basisWeight',
        render: (
          value: string,
          record: { category?: string; basisWeightMetric?: string }
        ) => basisWeight(value, { metric: record.basisWeightMetric, withUnit: false }),
      },
      {
        title: <Trans i18nKey="jobConfig.paperOption.color" />,
        width: 120,
        dataIndex: 'color',
        render: (value) => {
          return (
            <span className="form-search-table-textCapitalize">
              {value?.replaceAll('_', ' ')}
            </span>
          );
        },
      },
      {
        title: <Trans i18nKey="jobConfig.paperOption.brand" />,
        width: 240,
        dataIndex: 'brand',
        render: (value) => {
          return (
            <span className="form-search-table-textCapitalize">
              {value?.replaceAll('_', ' ').toLowerCase()}
            </span>
          );
        },
      },
      {
        title: columnTitle(
          t('jobConfig.paperOption.widthIn'),
          MeasureType.LENGTH
        ),
        width: 100,
        dataIndex: 'widthIn',
        render: (value: number, record: PlatformOptionPaper) => (
          <RevxMeasure
            value={value}
            metric={record.widthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
        ),      },
      {
        title: columnTitle(
          t('jobConfig.paperOption.lengthIn'),
          MeasureType.LENGTH
        ),
        width: 100,
        dataIndex: 'lengthIn',
        render: (value: number, record: PlatformOptionPaper) => (
          <RevxMeasure
            value={value}
            metric={record.lengthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
        ),      },
      {
        title: <Trans i18nKey="jobConfig.paperOption.materialConstraint1" />,
        width: 120,
        dataIndex: 'constraint1',
      },
      {
        title: <Trans i18nKey="jobConfig.paperOption.materialConstraint2" />,
        width: 120,
        dataIndex: 'constraint2',
      },
    ];
    const baseAction = {
      title: 'ACTION',
      width: 100,
      dataIndex: 'id',
      fixed: 'right' as 'left',
      render: (id: number, { equivalentCode }: PlatformOptionPaper) => {
        const isChecked = equivalentCode === sku?.paperEquivalentCode;
        return (
          <RevxButton
            className={`${
              isChecked ? 'revx-button-selected' : ''
            } form-search-table-background`}
            style={{ width: 60 }}
            onClick={() => {
              console.log('onChange', onChange);
              if (typeof onChange === 'function') {
                onChange({
                  paperEquivalentCode: !isChecked ? equivalentCode : '',
                  paperSearchScope: form.getFieldValue('searchScope'),
                });
              } else {
                console.warn('SearchTable onChange is not defined');
              }
            }}
          >
            {isChecked ? (
              <Trans i18nKey="common.selected" />
            ) : (
              <Trans i18nKey="common.select" />
            )}
          </RevxButton>
        );
      },
    };
    return [...columns, baseAction];
    // TODO
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSource, form, loading, sku?.paperEquivalentCode, onChange]);

  const formValuesChange = (
    value: Partial<FormChangeProps>,
    allValue: FormChangeProps
  ) => {
    setSearchData({
      ...allValue,
    });
  };

  return (
    <div className={`form-search-table`}>
      <div className="form-search-table-header">
        <Form form={form} onValuesChange={formValuesChange}>
          <RevxFlexLayout
            justify="justify-center"
            className="form-search-table-header-filter"
          >
            <label>
              <Trans i18nKey="jobConfig.paperOption.seeResultsSortedBy" />:
            </label>

            <RevxFlexLayout>
              <Form.Item name="searchScope" noStyle>
                <RevxRadio {...paperRadioProps} />
              </Form.Item>
            </RevxFlexLayout>

            <Form.Item name="category" noStyle>
              <Select
                allowClear
                placeholder="Category"
                className="form-search-table-select"
                style={{ minWidth: 170 }}
                dropdownStyle={{
                  textTransform: 'capitalize',
                }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {searchForm?.category.map((item) => (
                  <Option key={item} value={item}>
                    <Tooltip
                      placement="topLeft"
                      overlayClassName="form-search-table-textCapitalize"
                      title={item.replaceAll('_', ' ')}
                    >
                      {item.replaceAll('_', ' ')}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {form.getFieldValue('category') === 'wide_format' ? (
              <Form.Item name="subCategories" noStyle>
                <Select
                  allowClear
                  placeholder="Sub Categories"
                  className="form-search-table-select"
                  style={{ minWidth: 150 }}
                  dropdownStyle={{
                    textTransform: 'capitalize',
                  }}
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                >
                  {searchForm?.subCategories?.map((item) => (
                    <Option key={item} value={item}>
                      <Tooltip
                        placement="topLeft"
                        overlayClassName="form-search-table-textCapitalize"
                        title={item.replaceAll('_', ' ')}
                      >
                        {item.replaceAll('_', ' ')}
                      </Tooltip>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <></>
            )}

            <Form.Item name="finish" noStyle>
              <Select
                allowClear
                placeholder="Finish"
                className="form-search-table-select"
                dropdownStyle={{
                  textTransform: 'capitalize',
                }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {searchForm?.finish.map((item) => (
                  <Option key={item} value={item}>
                    <Tooltip
                      placement="topLeft"
                      overlayClassName="form-search-table-textCapitalize"
                      title={item.replaceAll('_', ' ')}
                    >
                      {item.replaceAll('_', ' ')}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="basisWeight" noStyle>
              <Select
                allowClear
                placeholder="Basis Weight"
                className="form-search-table-select"
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {searchForm?.basisWeight.map((item) => (
                  <Option key={item} value={item}>
                    <Tooltip placement="topLeft" title={item}>
                      {item}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="color" noStyle>
              <Select
                allowClear
                placeholder="Color"
                className="form-search-table-select"
                style={{ minWidth: 120 }}
                dropdownStyle={{
                  textTransform: 'capitalize',
                }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {searchForm?.color.map((item) => (
                  <Option key={item} value={item}>
                    <Tooltip
                      placement="topLeft"
                      overlayClassName="form-search-table-textCapitalize"
                      title={item.replaceAll('_', ' ')}
                    >
                      {item.replaceAll('_', ' ')}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="brand" noStyle>
              <Select
                allowClear
                placeholder="Brand"
                className="form-search-table-select"
                style={{ minWidth: 170 }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {searchForm?.brand.map((item) => (
                  <Option key={item} value={item}>
                    <Tooltip placement="topLeft" title={item}>
                      {item}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </RevxFlexLayout>
          <div className="form-search-table-header-input">
            <Form.Item name="searchStr" noStyle>
              <Search placeholder="Enter Keywords to search" />
            </Form.Item>
          </div>
          <RevxFlexLayout
            align="center"
            justify="justify-center"
            className="form-search-table-header-pagination"
          >
            <div
              className={
                current === 0
                  ? 'form-search-table-header-pagination-disabled'
                  : 'form-search-table-header-pagination-action'
              }
              onClick={() => {
                current && setCurrent(current - 1);
              }}
            >
              <JobconfigPaperPreIcon />
            </div>
            <p className="form-search-table-unSelect">
              <Trans
                i18nKey="jobConfig.paperOption.showingTotalPapers"
                values={{
                  current: `${
                    allFilterTableData.length
                      ? `${current * pageSize + 1}-${
                          allPage - 1 > current
                            ? (current + 1) * pageSize
                            : allFilterTableData.length
                        }`
                      : '0'
                  }`,
                  allPage: allFilterTableData.length,
                }}
                components={{ color: <span /> }}
              />
            </p>
            <div
              className={
                allPage - 1 === current
                  ? 'form-search-table-header-pagination-disabled'
                  : 'form-search-table-header-pagination-action'
              }
              onClick={() => {
                allPage - 1 > current && setCurrent(current + 1);
              }}
            >
              <JobconfigPaperNextIcon />
            </div>
          </RevxFlexLayout>
        </Form>
      </div>
      <RevxTable<PlatformOptionPaper>
        scroll={{ x: 1130 }}
        className="form-search-table-body"
        columns={renderColumns}
        pagination={false}
        dataSource={tableSource || []}
        key={'equivalentCode'}
      />
    </div>
  );
};
